import { HttpStatusCode } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { EmergencyErrorCode } from "../../../consts/emergency";

import {
  EmergencyBleType,
  EmergencyGpsType,
  EmergencyNfcType,
  EmergencyQrJsonCode,
  EmergencySituationType,
  GpsCallTimeoutIosMS,
  GpsCallTimeoutMS,
} from "../../../types/emergency";

import {
  getSituation,
  patchBle,
  patchGps,
  patchNfc,
  patchQr,
} from "../../../services/emergencyServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useEvacuationManger from "../../../hooks/useEvacuationManager";
import useQr from "../../../hooks/useQr";
import useGPSInstant from "../../../hooks/useGPSInstant";

import { MyProfileAlertList } from "../../MyInfo";
import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonButton from "../../../components/Button";
import CommonPopup from "../../../components/Popup";
import ExpiredSituation from "./Expired";
import NaverMapWithSkeleton from "../NaverMapWithSkeleton";
import SituationStatusButtonGroup from "../StatusButtonGroup";
import NFCReader from "../../../components/NFCReader";
import ScanningBluetooth from "./ScanningBluetooth";

import QrImage from "../../../assets/img/emergency/ee-qr.png";
import BluetoothImage from "../../../assets/img/emergency/ee-bluetooth.png";
import PrimaryButton from "../../../components/Button/Primary";
import { useDeviceType } from "../../../hooks/useDeviceType/Index";
import PinchZoomImage from "../../../components/PinchZoomImage";
import { findOptimizedImgUrl } from "../../../libs/image";
// import NfcImage from "../../../assets/img/emergency/ee-nfc.png";

type Props = {};

export default function Situation(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const [popup, setPopup] = useState<
    | "help"
    | "helpDetail"
    | "vacation"
    | "evacuation"
    | "updateHelpDetail"
    | "emergencyScanner"
    | "GpsDetail"
    | "GpsFail"
    | "anotherMethod"
    | "qrSuccess"
    | "nfcSuccess"
    | "bleSuccess"
    | ""
  >("");
  const [mode, setMode] = useState<"NFC" | "Bluetooth" | undefined>(undefined);

  const [situation, setSituation] = useState<
    EmergencySituationType | undefined
  >(undefined);
  const [isShelterImgOpen, setIsShelterImgOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isQrLoading, setIsQrLoading] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isManager, _, { user }] = useEvacuationManger();
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isQrLoading,
  });
  const deviceType = useDeviceType();
  const [gpsResult, callGps] = useGPSInstant({});
  const [qrResult, qrCall] = useQr({ open });

  const manualGPSCall = useRef<Boolean>(false);

  const gpsCallTimeOut = useRef<NodeJS.Timeout | null>(null);

  const gpsTimeoutMS =
    deviceType === "iOS" ? GpsCallTimeoutIosMS : GpsCallTimeoutMS;

  const handleGpsPatch = async () => {
    setIsLoading(true);
    manualGPSCall.current = true;

    gpsCallTimeOut.current = setTimeout(() => {
      if (manualGPSCall.current === true) {
        // 대피 완료를 누른 뒤 아직 네이티브로 부터 gps 결과를 받지 못한 경우
        manualGPSCall.current = false;
        setPopup("GpsFail");
        setIsLoading(false);
        open("위치 정보 요청 시간이 초과되었습니다.");
      }
    }, gpsTimeoutMS);

    callGps();
  };

  const handleQrPatchCallback = async (qrResult: EmergencyQrJsonCode) => {
    try {
      if (!situation?.evacuationId) {
        throw new Error("상황 정보가 없습니다.");
      }

      const response = await patchQr(situation?.evacuationId, qrResult);
      if (response.status === HttpStatusCode.Ok) {
        setPopup("qrSuccess");
      } else {
        openError(response, "QR 인증에 실패하였습니다.");
      }
    } catch (e) {
      openError(e, "QR 인증에 실패하였습니다.");
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleNfcPatchCallback = async (nfcResult: EmergencyNfcType) => {
    try {
      if (!situation?.evacuationId) {
        throw new Error("상황 정보가 없습니다.");
      }

      const response = await patchNfc(situation?.evacuationId, nfcResult);
      if (response.status === HttpStatusCode.Ok) {
        setPopup("nfcSuccess");
      } else {
        openError(response, "NFC 인증에 실패하였습니다.");
      }
    } catch (e) {
      openError(e, "NFC 인증에 실패하였습니다.");
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleBlePatchCallback = async (params: EmergencyBleType) => {
    setIsQrLoading(true);
    try {
      if (!situation?.evacuationId) {
        throw new Error("상황 정보가 없습니다.");
      }

      const response = await patchBle(situation?.evacuationId, params);
      if (response.status === HttpStatusCode.Ok) {
        setMode(undefined);
        setPopup("bleSuccess");
      } else {
        openError(response, "BLE 인증에 실패하였습니다.");
      }
    } catch (e) {
      openError(e, "BLE 인증에 실패하였습니다.");
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleGpsPatchCallback = async (gpsResult: EmergencyGpsType) => {
    try {
      if (!situation?.evacuationId) {
        throw new Error("상황 정보가 없습니다.");
      }

      const response = await patchGps(situation?.evacuationId, gpsResult);
      if (response.status === HttpStatusCode.Ok) {
        setPopup("GpsDetail");
      } else {
        setPopup("GpsFail");
      }
    } catch (e) {
      if (popup === "GpsFail") {
        openError(e, "GPS 인증에 실패하였습니다.");
      } else {
        setPopup("GpsFail");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (gpsResult.isLoading === false && gpsResult.result) {
      if (manualGPSCall.current === true) {
        manualGPSCall.current = false;

        if (gpsCallTimeOut.current) {
          clearTimeout(gpsCallTimeOut.current);
        }
        if (gpsResult.result.error) {
          setPopup("GpsFail");
          setIsLoading(false);
        } else if (
          typeof gpsResult.result.lat === "number" &&
          typeof gpsResult.result.lng === "number"
        ) {
          handleGpsPatchCallback({
            lat: gpsResult.result.lat,
            lng: gpsResult.result.lng,
          });
        } else {
          setPopup("GpsFail");
          setIsLoading(false);
        }
      }
    }
  }, [gpsResult]);

  useEffect(() => {
    if (qrResult.isLoading === false) {
      if (qrResult.result) {
        const qrJson = JSON.parse(qrResult.result);
        // open(
        //   `QR 인증 성공 shelterId: ${qrJson["shelterId"]} createdAdminId: ${qrJson["createdAdminId"]}`
        // );
        handleQrPatchCallback({
          shelterId: qrJson["shelterId"],
          createdAdminId: qrJson["createdAdminId"],
        });
      } else {
        open("QR 인증 실패");
        setIsQrLoading(false);
      }
    }
  }, [qrResult]);

  const loadSituation = async () => {
    setIsLoading(true);
    try {
      const response = await getSituation();
      if (response.status === HttpStatusCode.Ok) {
        if (response.data.status === "ENDED") {
          navigate("/evacuation/done", { state: { situation: response.data } });
          return;
        } else {
          setSituation(response.data);
        }
      } else {
        throw new Error("상황 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.code === EmergencyErrorCode.EXPIRED) {
        setIsExpired(true);
      } else {
        openError(e, "상황 정보를 불러오지 못했습니다.", {
          onClick: loadSituation,
          buttonText: "재시도",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isLoading === false) {
      loadSituation();

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          loadSituation();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
  }, [user?.isLoading]);

  const moveToResult = () => {
    navigate(`/evacuation/evacuation-result`, {
      state: {
        situation,
        isManager,
        referer: location?.pathname ?? "evacuation/situation",
      },
    });
  };

  const handleClickShelterImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsShelterImgOpen(true);
  };
  const handleCloseShelterImg = (e: React.MouseEvent) => {
    setIsShelterImgOpen(false);
  };

  return (
    <>
      {loadingComponent}
      {isExpired ? (
        <ExpiredSituation />
      ) : (
        <>
          <div className="px-5 w-full">
            <StickyHeader
              title="비상 대피"
              rightContent={
                <div
                  className="flex-1 flex gap-1 items-center justify-center"
                  onClick={
                    situation?.evacuationId
                      ? () =>
                          navigate("/evacuation/emergency-alarm", {
                            state: { situationId: situation?.evacuationId },
                          })
                      : () => open("잠시 후 시도해 주세요.")
                  }
                >
                  <MyProfileAlertList />
                </div>
              }
            />
          </div>
          <div className="min-h-screen z-30 bg-white w-full">
            <div className={clsx([{ hidden: mode !== undefined }])}>
              <NaverMapWithSkeleton
                situation={situation}
                cert={true}
                gpsResult={gpsResult}
                callGps={callGps}
                handleClickShelterImg={handleClickShelterImg}
              />
            </div>
            <SituationStatusButtonGroup
              situation={situation}
              handleGps={handleGpsPatch}
              refresh={loadSituation}
            />
            <CommonPopup
              isOpen={popup === "GpsFail"}
              onClose={() => setPopup("")}
              padding={0}
              title="GPS 인증 실패"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center gap-5">
                <div>
                  <img
                    src={findOptimizedImgUrl(situation?.shelterImg?.items)}
                    alt="shelterImg"
                    className="w-[51vw] h-[32vw] object-fill rounded-[10px]"
                  />
                </div>
                <div className="flex flex-col gap-3 items-center">
                  <div className="text-base font-bold text-lg leading-[19.36px]">
                    {situation?.shelterName}
                  </div>
                  <div className="text-sm leading-[16.94px]">
                    {situation?.shelterDescription}
                  </div>
                </div>
                <div className="flex flex-col gap-3 items-center px-2">
                  <div className="text-xl leading-[26px] font-bold pb-4">
                    대피장소로 이동 후 다시 시도해주세요.
                  </div>
                </div>
                <PrimaryButton
                  className="rounded-[15px] w-[calc(100%-100px)] h-[40px] font-bold text-lg"
                  onClick={() => {
                    setPopup("anotherMethod");
                  }}
                >
                  다른 방식으로 인증하기
                </PrimaryButton>

                <div className="flex w-full">
                  <CommonButton
                    variant="secondary"
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => setPopup("")}
                  >
                    닫기
                  </CommonButton>
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={handleGpsPatch}
                  >
                    다시시도
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>
            <CommonPopup
              isOpen={popup === "GpsDetail"}
              onClose={() => moveToResult()}
              padding={0}
              title="GPS 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    GPS 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            <CommonPopup
              isOpen={popup === "anotherMethod"}
              onClose={() => setPopup("")}
              padding={0}
              title="대피 완료 인증"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center gap-7">
                <div className="flex gap-3 w-[calc(100vw-40px)]">
                  <div
                    className="flex-1 border rounded-[0.75px] flex flex-col gap-2 items-center whitespace-pre-line text-center p-3 text-sm leading-[17px] font-medium"
                    onClick={() => {
                      setIsQrLoading(true);
                      qrCall();
                    }}
                  >
                    <img src={QrImage} alt="qr" className="w-[40px] h-[40px]" />
                    QR 인증
                  </div>
                  <div
                    className={clsx([
                      "flex-1 border rounded-[0.75px] flex flex-col gap-2 items-center whitespace-pre-line text-center p-3 text-sm leading-[17px] font-medium",
                    ])}
                    onClick={() => {
                      setMode("Bluetooth");
                    }}
                  >
                    <img
                      src={BluetoothImage}
                      alt="bluetooth"
                      className="w-[40px] h-[40px]"
                    />
                    {"Bluetooth\n인증"}
                  </div>
                  {/* <div
                    className="flex-1 border rounded-[0.75px] flex flex-col gap-2 items-center whitespace-pre-line text-center p-3 text-sm leading-[17px] font-medium"
                    onClick={() => {
                      setMode("NFC");
                    }}
                  >
                    <img
                      src={NfcImage}
                      alt="nfc"
                      className="w-[40px] h-[40px]"
                    />
                    NFC 인증
                  </div> */}
                </div>

                <div className="flex w-full">
                  <CommonButton
                    variant="secondary"
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => setPopup("")}
                  >
                    닫기
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            <CommonPopup
              isOpen={popup === "qrSuccess"}
              onClose={() => moveToResult()}
              padding={0}
              title="QR 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    QR 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>
            <CommonPopup
              isOpen={popup === "nfcSuccess"}
              onClose={() => moveToResult()}
              padding={0}
              title="NFC 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    NFC 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>
            <CommonPopup
              isOpen={popup === "bleSuccess"}
              onClose={() => moveToResult()}
              padding={0}
              title="BLE 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    BLE 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            {mode === "NFC" && (
              <NFCReader
                onClose={() => setMode(undefined)}
                onResult={(result) => {
                  handleNfcPatchCallback(result);
                }}
              />
            )}
            {mode === "Bluetooth" && (
              <ScanningBluetooth
                onResult={(result) => {
                  handleBlePatchCallback(result);
                }}
                onClose={() => setMode(undefined)}
              />
            )}
            {situation &&
              situation.shelterImg &&
              situation.shelterImg.items.length > 0 && (
                <PinchZoomImage
                  imageList={[
                    findOptimizedImgUrl(situation?.shelterImg?.items) as string,
                  ]}
                  isOpen={isShelterImgOpen}
                  onClose={handleCloseShelterImg}
                />
              )}

            {simplePopupComponent}
          </div>
        </>
      )}
    </>
  );
}
